/**
 * Whether the live request-flow animation (the particle overlay) is drawn.
 *
 * OFF by default: the animated dots are eye-catching and distracting for
 * everyday modelling, so a fresh board is calm and the user opts in via the
 * "Live" header toggle. The choice persists per browser (localStorage), so it
 * sticks across sessions and repos.
 *
 * A module-level store (not context) because a board has exactly one live-flow
 * layer and one toggle, and they sit in different parts of the tree (a board
 * layer vs the header chrome) — the same bridge `driftTopologyStore` uses.
 */
import { useSyncExternalStore } from "react";

const KEY = "arch:flow-live";
import { hostStorage } from "../../host/hostStorage.js";
const listeners = new Set<() => void>();

function read(): boolean {
  return hostStorage.getItem(KEY) === "1";
}

let current = read();

export function getFlowVisible(): boolean {
  return current;
}

export function setFlowVisible(on: boolean): void {
  if (on === current) return;
  current = on;
  hostStorage.setItem(KEY, on ? "1" : "0");
  for (const l of listeners) l();
}

export function toggleFlowVisible(): void {
  setFlowVisible(!current);
}

export function subscribeFlowVisible(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** Test-only: reset to the default (off) without touching storage semantics. */
export function _resetFlowVisible(): void {
  current = false;
  hostStorage.removeItem(KEY);
  for (const l of listeners) l();
}

/** Subscribe a component to the live-flow visibility flag. */
export function useFlowVisible(): boolean {
  return useSyncExternalStore(subscribeFlowVisible, getFlowVisible, getFlowVisible);
}
